import React, { useState } from "react";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Linkedin, Sparkles, Loader2, Check } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

export default function LinkedInEnrichDialog({ open, onClose, lead, onApplied }) {
  const [url, setUrl] = useState(lead?.linkedin_url || "");
  const [manualText, setManualText] = useState("");
  const [loading, setLoading] = useState(false);
  const [applying, setApplying] = useState(false);
  const [suggested, setSuggested] = useState(null);
  const [selected, setSelected] = useState({});

  const fetchProfile = async () => {
    if (!url && !manualText) { toast.error("Enter a LinkedIn URL or paste profile text"); return; }
    setLoading(true); setSuggested(null);
    try {
      const res = await api.post(`/leads/${lead.lead_id}/enrich-linkedin`, {
        linkedin_url: url || undefined,
        manual_text: manualText || undefined,
      });
      setSuggested(res.data.suggested);
      const initial = {};
      Object.entries(res.data.suggested).forEach(([k, v]) => { if (v) initial[k] = true; });
      setSelected(initial);
      toast.success("Profile parsed");
    } catch (e) {
      const msg = e?.response?.data?.detail || "Enrichment failed";
      toast.error(msg);
      if (String(msg).toLowerCase().includes("could not fetch")) {
        // Encourage manual paste
        setManualText((t) => t || "");
      }
    } finally { setLoading(false); }
  };

  const applyToLead = async () => {
    if (!suggested) return;
    setApplying(true);
    try {
      const patch = {};
      const map = {
        name: "name", title: "title", company: "company", location: "location",
        segment: "segment", picture_url: "picture_url", linkedin_url: "linkedin_url",
      };
      Object.entries(map).forEach(([sk, lk]) => {
        if (selected[sk] && suggested[sk]) patch[lk] = suggested[sk];
      });
      if (selected.headline && suggested.headline) {
        patch.notes = (lead.notes ? lead.notes + "\n\n" : "") + `LinkedIn headline: ${suggested.headline}`;
      }
      if (!Object.keys(patch).length) { toast.error("Nothing selected to apply"); setApplying(false); return; }
      await api.patch(`/leads/${lead.lead_id}`, patch);
      toast.success("Lead updated from LinkedIn");
      onApplied?.();
      onClose();
      setSuggested(null);
    } catch (_e) { toast.error("Apply failed"); }
    finally { setApplying(false); }
  };

  const FIELDS = [
    { key: "name", label: "Name" },
    { key: "title", label: "Title" },
    { key: "company", label: "Company" },
    { key: "location", label: "Location" },
    { key: "segment", label: "Segment" },
    { key: "headline", label: "Headline (append to notes)" },
    { key: "linkedin_url", label: "LinkedIn URL" },
    { key: "picture_url", label: "Profile photo" },
  ];

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) { onClose(); setSuggested(null); } }}>
      <DialogContent data-testid="linkedin-dialog" className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="font-serif-display text-2xl flex items-center gap-2">
            <Linkedin className="w-5 h-5 text-[#0A66C2]" /> Enrich from LinkedIn — {lead?.name}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <Label>LinkedIn profile URL</Label>
            <Input
              data-testid="linkedin-url-input"
              placeholder="https://www.linkedin.com/in/…"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
            />
          </div>

          <div>
            <Label className="flex items-center justify-between">
              <span>Or paste profile summary (if URL is blocked by LinkedIn)</span>
            </Label>
            <Textarea
              data-testid="linkedin-manual-text"
              rows={5}
              value={manualText}
              onChange={(e) => setManualText(e.target.value)}
              placeholder="Paste name, title, company, headline, location from the profile page…"
            />
          </div>

          <Button
            data-testid="linkedin-parse-btn"
            onClick={fetchProfile}
            disabled={loading}
            className="bg-[#0A66C2] text-white hover:bg-[#0958a8]"
          >
            {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Parsing…</> : <><Sparkles className="w-4 h-4 mr-2" /> Parse with Claude</>}
          </Button>

          {suggested && (
            <div className="border border-[#E2E8F0] rounded-md p-4 bg-[#F9FAFB]">
              <div className="label-eyebrow mb-3">Suggested — tick fields to apply</div>
              <div className="flex items-start gap-4">
                {suggested.picture_url && (
                  <img src={suggested.picture_url} alt="" className="w-20 h-20 rounded-full object-cover border border-[#E2E8F0]" />
                )}
                <div className="flex-1 space-y-2">
                  {FIELDS.map((f) => {
                    const v = suggested[f.key];
                    if (!v) return null;
                    return (
                      <label key={f.key} className="flex items-start gap-2 text-sm cursor-pointer" data-testid={`enrich-field-${f.key}`}>
                        <input
                          type="checkbox"
                          checked={!!selected[f.key]}
                          onChange={(e) => setSelected({ ...selected, [f.key]: e.target.checked })}
                          className="mt-1"
                        />
                        <span className="w-32 shrink-0 text-slate-500 text-xs uppercase tracking-widest pt-0.5">{f.label}</span>
                        <span className="flex-1 text-slate-800 break-words">
                          {f.key === "picture_url" ? <span className="text-xs text-slate-500 break-all">{v}</span> : v}
                        </span>
                      </label>
                    );
                  })}
                </div>
              </div>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Close</Button>
          {suggested && (
            <Button data-testid="linkedin-apply-btn" onClick={applyToLead} disabled={applying} className="bg-[#0A1128] text-white hover:bg-[#152243]">
              {applying ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Applying…</> : <><Check className="w-4 h-4 mr-2" /> Apply selected fields</>}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
